import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import TasksPanel from './components/TasksPanel';
import ToastProvider from './components/Toasts';
import ErrorBoundary from './components/ErrorBoundary';
import AccessGate from './components/AccessGate';
import Icon from './components/Icon';
import Spinner from './components/Spinner';
import { SkeletonCard } from './components/Skeleton';
import { JobsProvider } from './hooks/useJobs';
import useTheme from './theme';
import { PRODUCT, PUBLISHER, PUBLISHER_LOGO_URL } from './brand';

// Chaque page arrive dans son propre fichier : le client qui ouvre sa proposition
// ne télécharge pas le tableau de bord, et personne ne charge jsPDF avant de
// demander un PDF.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NewClient = lazy(() => import('./pages/NewClient'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const PublicProposal = lazy(() => import('./pages/PublicProposal'));
const Settings = lazy(() => import('./pages/Settings'));
const Tasks = lazy(() => import('./pages/Tasks'));

const LINKS = [
  { to: '/', label: 'Tableau de bord', end: true },
  { to: '/taches', label: 'Tâches' },
  { to: '/reglages', label: 'Réglages' },
];

function ThemeButton() {
  const { resolved, toggle } = useTheme();
  const dark = resolved === 'dark';
  return (
    <button
      type="button"
      className="icon-btn nav-theme"
      onClick={toggle}
      aria-label={dark ? 'Passer en thème clair' : 'Passer en thème sombre'}
      title={dark ? 'Thème clair' : 'Thème sombre'}
    >
      <Icon name={dark ? 'sun' : 'moon'} />
    </button>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const wide = useRef(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 861px)') : null
  );

  // La barre ne se détache qu'une fois le contenu passé dessous.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Le tiroir se referme au changement de page et dès que l'écran redevient large,
  // sinon il resterait ouvert, invisible, en capturant le clavier.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const mq = wide.current;
    if (!mq) return undefined;
    const onChange = (e) => { if (e.matches) setOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="inner">
        <Link to="/" className="brand">
          <img src={PUBLISHER_LOGO_URL} alt="" />
          <span className="brand-text">
            <span>{PRODUCT.name} {PRODUCT.suffix}</span>
            <span className="brand-publisher">par {PUBLISHER.name}</span>
          </span>
        </Link>

        <div className="nav-links" id="nav-links" hidden={!open}>
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="nav-actions">
          <ThemeButton />
          <Link to="/nouveau" className="btn small nav-new">
            <Icon name="plus" size={15} /> <span>Nouveau client</span>
          </Link>
          <button
            type="button"
            className="icon-btn nav-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="nav-links"
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            <Icon name={open ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
    </nav>
  );
}

/** Remet la page en haut à chaque navigation : sans cela on arrive au milieu. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [pathname]);
  return null;
}

/** Ce qu'on voit le temps qu'une page arrive : la même ossature qu'en chargement de données. */
function PageFallback() {
  return (
    <div className="container" aria-busy="true">
      <SkeletonCard lines={2} />
      <div className="spacer" />
      <SkeletonCard lines={4} />
    </div>
  );
}

export default function App() {
  // Le thème initial est posé par le script en tête de `index.html` (sans quoi
  // la page clignote) ; seul le basculeur en modifie l'état ensuite.
  return (
    <ToastProvider>
      <ScrollToTop />
      <Routes>
        {/* Vue publique partagée au client (sans navbar interne, sans polling des tâches) */}
        <Route
          path="/p/:token"
          element={
            <ErrorBoundary title="Votre proposition n'a pas pu s'afficher">
              <Suspense fallback={<div className="container"><Spinner label="Chargement de vos propositions…" /></div>}>
                <PublicProposal />
              </Suspense>
            </ErrorBoundary>
          }
        />

        <Route
          path="*"
          element={
            <AccessGate>
            <JobsProvider>
              <a className="skip-link" href="#contenu">Aller au contenu</a>
              <Navbar />
              <main id="contenu">
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/nouveau" element={<NewClient />} />
                      <Route path="/reglages" element={<Settings />} />
                      <Route path="/taches" element={<Tasks />} />
                      <Route path="/clients/:id" element={<ClientDetail />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>
              <TasksPanel />
            </JobsProvider>
            </AccessGate>
          }
        />
      </Routes>
    </ToastProvider>
  );
}
